import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "About", id: "about" },
  { label: "Services", id: "services" },
  { label: "Gallery", id: "gallery" },
  { label: "Testimonials", id: "testimonials" },
  { label: "Contact", id: "contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 320);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-background/70 border-b border-border" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-12 h-24 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => go("top")} className="flex items-center gap-3 group">
          <img src="/logo.png" alt="Klean With Katie" className="h-16 w-16 md:h-[68px] md:w-[68px] object-contain rounded-full" />
          <span className="hidden sm:block leading-tight text-left">
            <span className="block font-serif text-2xl text-espresso tracking-tight">
              Klean <span className="italic text-blush">with</span> Katie
            </span>
            <span className="block text-[10px] uppercase tracking-[0.22em] text-cocoa">Clean Spaces, Happy Faces</span>
          </span>
        </button>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}-link`}
              onClick={() => go(l.id)}
              className="text-xs uppercase tracking-[0.22em] text-cocoa hover:text-blush transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-quote-button"
            onClick={() => go("contact")}
            className="text-xs uppercase tracking-[0.22em] px-6 py-3 bg-espresso text-cream hover:bg-blush transition-colors duration-300 rounded-full"
          >
            Request a Quote
          </button>
        </div>

        <button data-testid="nav-mobile-toggle" className="md:hidden text-espresso" onClick={() => setOpen(!open)}>
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-background/95 backdrop-blur-xl border-b border-border"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.id}-link`}
                  onClick={() => go(l.id)}
                  className="text-left font-serif text-2xl text-espresso"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-quote-button"
                onClick={() => go("contact")}
                className="mt-2 text-xs uppercase tracking-[0.22em] px-6 py-4 bg-espresso text-cream rounded-full"
              >
                Request a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
